import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { gbp, num } from "@/lib/format";
import { downloadCsv } from "@/lib/csv";
import { Download, Receipt, PiggyBank, TrendingUp, Warehouse } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export default function SummaryOverview() {
  const [fy, setFy] = useState("All");
  const { data, isLoading } = useQuery({
    queryKey: ["summary", fy],
    queryFn: () => api.get("/summary", { params: { fy: fy === "All" ? "" : fy } }).then((r) => r.data),
  });

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const fyOptions = ["All", ...(data?.fy_options || [])];
  const chartData = rows.map((r) => ({ name: r.site, "Cost of Goods": r.cost_of_goods, Savings: r.savings }));

  const exportCsv = () => downloadCsv(`epgroup-all-site-summary-${fy}.csv`, ["site", "delivery", "emergency", "cost_of_goods", "savings", "pulled_forward", "rate_lines"], rows);

  if (isLoading) return <Loading />;

  const Metric = ({ icon: Icon, label, value, tint }) => (
    <Card className="p-5 border-slate-200 dark:border-slate-800">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}><Icon className="w-5 h-5" /></div>
      <p className="mt-4 text-2xl font-display font-bold font-mono-num text-slate-900 dark:text-white">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
    </Card>
  );

  return (
    <div>
      <PageHeader title="All-Site Summary" subtitle="Top-level view compiling cost of goods, savings and rates across every site.">
        <Select value={fy} onValueChange={setFy}>
          <SelectTrigger className="w-full sm:w-36" data-testid="summary-fy-select"><SelectValue /></SelectTrigger>
          <SelectContent>{fyOptions.map((f) => <SelectItem key={f} value={f}>{f === "All" ? "All years" : f}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={exportCsv} data-testid="summary-export-button"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-6">
        <Metric icon={Warehouse} label="Sites" value={num(totals.sites)} tint="bg-indigo-50 text-indigo-600" />
        <Metric icon={Receipt} label="Total Cost of Goods" value={gbp(totals.cost_of_goods)} tint="bg-amber-50 text-amber-600" />
        <Metric icon={PiggyBank} label="Total Savings" value={gbp(totals.savings)} tint="bg-emerald-50 text-emerald-600" />
        <Metric icon={TrendingUp} label="Service Credits Due" value={gbp(totals.service_credits)} tint="bg-rose-50 text-rose-600" />
      </div>

      <Card className="p-5 border-slate-200 dark:border-slate-800 mb-8">
        <h3 className="font-display font-semibold mb-4 text-slate-900 dark:text-white">Cost of Goods vs Savings by site</h3>
        {chartData.some((d) => d["Cost of Goods"] || d.Savings) ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={70} interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => gbp(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Cost of Goods" fill="#d97706" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Savings" fill="#059669" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState message="No cost/savings recorded against sites yet — add a Site to Cost of Goods & Savings records." />}
      </Card>

      <TableShell>
        <table className="w-full text-sm" data-testid="summary-table">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              <th className="px-4 py-3">Site</th>
              <th className="px-4 py-3 text-right">Delivery (Y1)</th>
              <th className="px-4 py-3 text-right">Emergency (Y1)</th>
              <th className="px-4 py-3 text-right">Cost of Goods</th>
              <th className="px-4 py-3 text-right">Savings</th>
              <th className="px-4 py-3 text-right">Pulled Forward</th>
              <th className="px-4 py-3 text-right">Rate Lines</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((r) => (
              <tr key={r.site} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`summary-row-${r.site}`}>
                <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">{r.site}</td>
                <td className="px-4 py-3 text-right font-mono-num">{gbp(r.delivery)}</td>
                <td className="px-4 py-3 text-right font-mono-num">{gbp(r.emergency)}</td>
                <td className="px-4 py-3 text-right font-mono-num">{gbp(r.cost_of_goods)}</td>
                <td className="px-4 py-3 text-right font-mono-num text-emerald-600">{gbp(r.savings)}</td>
                <td className="px-4 py-3 text-right font-mono-num text-blue-600">{gbp(r.pulled_forward)}</td>
                <td className="px-4 py-3 text-right font-mono-num text-slate-500">{r.rate_lines}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 dark:bg-slate-800/60 font-semibold">
              <td className="px-4 py-3" colSpan={3}>Totals ({totals.sites} sites)</td>
              <td className="px-4 py-3 text-right font-mono-num">{gbp(totals.cost_of_goods)}</td>
              <td className="px-4 py-3 text-right font-mono-num text-emerald-700">{gbp(totals.savings)}</td>
              <td className="px-4 py-3 text-right font-mono-num text-blue-700">{gbp(totals.pulled_forward)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
        {rows.length === 0 && <EmptyState message="No sites found." />}
      </TableShell>
    </div>
  );
}
